import { Injectable } from '@angular/core';

import { UtilityService } from '../../../core/utility/services/utility.service';
import { Configuration, DataSource, Product, Routing } from '../interfaces/configuration.interface';
import { Layout } from '../interfaces/layout.interface';
import { Partner } from '../interfaces/partner.interface';
import { PermissionsObj, PermissionsWithBooleanCheckObj } from '../interfaces/permission.interface';
import {
	PrintableReport,
	SlideDataConfiguration,
	SlideWidgetDataConfiguration,
	SupplementalCampaigns
} from '../interfaces/printable-reports.interface';
import {
	CheckboxButton,
	ColorConfig,
	ColumnConfig,
	ComplexPropertyConfiguration,
	DataTableConfig,
	DownloadConfig,
	Query,
	RadioButtonGroup,
	ReportingConfig,
	ReportingConfigObject,
	SeriesViewConfiguration,
	Widget
} from '../interfaces/widget.interface';
import { ChartConfigurationMapperService } from './chart-configuration.mapper.service';

import * as _ from 'underscore';

/**
 *
 */
@Injectable()
export class ConfigurationMapperService {
	/**
	 *
	 */
	constructor(private chartConfigurationMapperService: ChartConfigurationMapperService) {}

	/**
	 *
	 * @param configurationResponse
	 * @returns {Configuration}
	 */
	mapConfigurationResponseToInterface(configurationResponse: any): Configuration {
		const configuration: Configuration = {} as Configuration;
		configuration.useDefaultConfig = configurationResponse.useDefaultConfig
			? configurationResponse.useDefaultConfig
			: false;
		configuration.partner = this.mapPartnerResponseToInterface(configurationResponse);
		configuration.version = configurationResponse.version;
		configuration.directoryName = configurationResponse.directoryName;
		configuration.enableDatePickerInputBox = configurationResponse.enableDatePickerInputBox
			? configurationResponse.enableDatePickerInputBox
			: false;
		configuration.downloadIcon = configurationResponse.downloadIcon ? configurationResponse.downloadIcon : '';
		configuration.handleLockIcon = configurationResponse.handleLockIcon ? configurationResponse.handleLockIcon : '';
		configuration.closeHandleIcon = configurationResponse.closeHandleIcon ? configurationResponse.closeHandleIcon : '';
		configuration.openHandleIcon = configurationResponse.openHandleIcon ? configurationResponse.openHandleIcon : '';
		configuration.isNavigationCarousel = configurationResponse.isNavigationCarousel;
		configuration.useDefaultHtmls = configurationResponse.useDefaultHtmls;
		configuration.maxAdvertisersAllowed = configurationResponse.maxAdvertisersAllowed;
		configuration.styles = this.mapStylesResponseToInteface(
			configurationResponse.styles ? configurationResponse.styles : undefined
		);
		if (configurationResponse.layout) {
			configuration.layout = this.mapLayoutResponseToInterface(configurationResponse.layout);
		}
		configuration.dataSources = this.mapDataSourcesResponseToInterfae(
			configurationResponse.dataSources ? configurationResponse.dataSources : []
		);
		if (configurationResponse.routing) {
			configuration.routing = this.mapRoutingResponseToInterface(configurationResponse.routing);
		}
		configuration.widgets = this.mapWidgetsResponseToInterface(
			configurationResponse.widgets ? configurationResponse.widgets : [],
			''
		);
		configuration.overviewWidgets = this.mapWidgetsResponseToInterface(
			configurationResponse.overviewWidgets ? configurationResponse.overviewWidgets : [],
			'overview'
		);
		configuration.navWidgets = this.mapNavigationWidgetsResponseToInterface(
			configurationResponse.navigationWidgets ? configurationResponse.navigationWidgets : []
		);
		configuration.products = this.mapProductsResponseToInterface(
			configurationResponse.products ? configurationResponse.products : []
		);
		configuration.disclaimer = configurationResponse.disclaimer ? configurationResponse.disclaimer : '';
		configuration.overviewPrintableReport = configurationResponse.overviewPrintableReport;
		return configuration;
	}

	mapStylesResponseToInteface(stylesResponse: any): string[] {
		const styles: string[] = stylesResponse;
		return styles;
	}

	/**
	 *
	 * @param partnerResponse
	 * @returns {Partner}
	 */
	mapPartnerResponseToInterface(partnerResponse: any): Partner {
		const partner: Partner = {} as Partner;
		partner.partnerName = partnerResponse.partnerName;
		partner.isMarketron = partnerResponse.isMarketron ? partnerResponse.isMarketron : false;
		partner.directoryName = partnerResponse.directoryName;
		partner.filterAdvertisers = !!partnerResponse.filterAdvertisers;
		partner.partnerLogo = partnerResponse.logoURL;
		return partner;
	}

	/**
	 *
	 * @param routingResponse
	 * @returns {Partner}
	 */
	mapRoutingResponseToInterface(routingResponse: any): Routing {
		const routing: Routing = {} as Routing;
		if (routingResponse.defaultRoute) {
			routing.defaultRoute = routingResponse.defaultRoute;
		}
		return routing;
	}

	/**
	 *
	 * @param productsResponse
	 * @returns {Product[]}
	 */
	mapProductsResponseToInterface(productsResponse: any): Product[] {
		const products: Product[] = [];
		for (const productResp of productsResponse) {
			products.push(this.mapProductResponseToInterface(productResp));
		}
		return products;
	}

	/**
	 *
	 * @param productResponse
	 * @returns {Product}
	 */
	mapProductResponseToInterface(productResponse: any): Product {
		const product: Product = {} as Product;
		product.productName = productResponse.productName;
		product.productURL = productResponse.productUrl;
		product.printableStrategyName = productResponse.printableStrategyName;
		product.isCarousel = productResponse.isCarousel;
		product.isTab = productResponse.isTab;
		product.enableLeftNavWidgets = productResponse.enableLeftNavWidgets;
		product.widgets = this.mapWidgetsResponseToInterface(
			productResponse.widgets ? productResponse.widgets : [],
			productResponse.productUrl
		);
		product.overviewWidgets = this.mapWidgetsResponseToInterface(
			productResponse.overviewWidgets ? productResponse.overviewWidgets : [],
			productResponse.productUrl
		);

		product.printableReport = this.mapPrintableReportResponseToInterface(productResponse.printableReport);
		product.enableProductDownloadConfig = productResponse.enableProductDownloadConfig;
		product.productCsvFilename = productResponse.productCsvFilename;
		product.uniqueOverwriteID = productResponse.uniqueOverwriteID;
		if (productResponse.printableModalPopUp !== undefined) {
			product.printableModalPopUp = productResponse.printableModalPopUp;
		}
		return product;
	}

	/**
	 *
	 * @param layoutResponse
	 * @returns {null}
	 */
	mapLayoutResponseToInterface(layoutResponse: any): Layout {
		const layout: Layout = {} as Layout;
		layout.showBreadcrumb = layoutResponse.showBreadcrumb;
		layout.showNavigation = layoutResponse.showNavigation;
		layout.showContent = layoutResponse.showContent;
		return layout;
	}

	/**
	 *
	 * @param datasourcesResponse
	 * @returns {DataSource[]}
	 */
	mapDataSourcesResponseToInterfae(datasourcesResponse: any[]): DataSource[] {
		const dataSources: DataSource[] = [];
		for (const datasourceResp of datasourcesResponse) {
			const dataSource: DataSource = {} as DataSource;
			dataSource.id = datasourceResp.name;
			dataSource.name = datasourceResp.name;
			dataSource.selected = datasourceResp.selected;
			dataSources.push(dataSource);
		}
		return dataSources;
	}

	/**
	 *
	 * @param widgetsResponse
	 * @returns {null}
	 */
	mapWidgetsResponseToInterface(widgetsResponse: any, productUrl: string): Widget[] {
		const widgets: Widget[] = [];

		for (const widgetResponse of widgetsResponse) {
			widgets.push(this.mapWidgetResponseToInterface(widgetResponse, productUrl));
		}

		return widgets;
	}

	/**
	 *
	 * @param widgetResponse
	 * @returns {null}
	 */
	mapWidgetResponseToInterface(widgetResponse: any, productUrl: string): Widget {
		const widget: Widget = {} as Widget;
		widget.widget = widgetResponse.widget;
		widget.widgetType = widgetResponse.widgetType;
		widget.widgetLayout = widgetResponse.widgetLayout;
		widget.sortDataBy = widgetResponse.sortDataBy;
		widget.defaultSort = widgetResponse.defaultSort;
		widget.impressionsViewedCompletionsRate = widgetResponse.impressionsViewedCompletionsRate
			? widgetResponse.impressionsViewedCompletionsRate
			: false;
		widget.externalWidgetsConfiguration = JSON.stringify(
			widgetResponse.externalWidgetsConfiguration ? widgetResponse.externalWidgetsConfiguration : {}
		);
		widget.noDataMessage = widgetResponse.noDataMessage;
		widget.widgetClass = widgetResponse.sectionClass;
		widget.daypartMetric = widgetResponse.daypartMetric;
		widget.containerClass = widgetResponse.containerClass;
		widget.description = widgetResponse.description;
		widget.widgetTitle = widgetResponse.sectionName;
		widget.dateFormat = widgetResponse.dateFormat ? widgetResponse.dateFormat : null;
		widget.tooltipInfo = widgetResponse.tooltipInfo;
		widget.placeholderImage = widgetResponse.imagePlaceholder;
		widget.markerIcon = widgetResponse.markerIcon;
		widget.markerIconHover = widgetResponse.markerIconHover;
		widget.colors = widgetResponse.colors;
		widget.colCount = widgetResponse.colCount;
		widget.timeseriesKey = widgetResponse.timeseriesKey;
		widget.path = widgetResponse.path;
		widget.productType = widgetResponse.productType;
		widget.query = this.mapQueryResponseToInterface(widgetResponse.query);
		widget.hasPreRollPlacementAccess = widgetResponse.hasPreRollPlacementAccess || false;
		widget.dataTableConfig = JSON.stringify(
			widgetResponse.dataTableConfig ? this.mapDataTableConfigToInterface(widgetResponse.dataTableConfig) : {}
		);
		widget.multiDatatableConfigurations = JSON.stringify(
			widgetResponse.multiDatatableConfigurations
				? this.mapMultiDataTableConfigToInterface(widgetResponse.multiDatatableConfigurations)
				: []
		);
		widget.colorConfig = JSON.stringify(
			widgetResponse.colorsConfig ? this.mapColorsConfigToInterface(widgetResponse.colorsConfig) : []
		);
		widget.chartConfiguration = JSON.stringify(
			widgetResponse.chartConfiguration
				? this.chartConfigurationMapperService.mapChartConfigToInterface(widgetResponse.chartConfiguration)
				: {}
		);
		widget.customChartConfiguration = JSON.stringify(
			widgetResponse.customChartConfiguration ? widgetResponse.customChartConfiguration : {}
		);
		widget.multiChartConfigurations = JSON.stringify(
			widgetResponse.multiChartConfigurations ? widgetResponse.multiChartConfigurations : []
		);
		widget.pieChartConfig = JSON.stringify(widgetResponse.pieChartConfig ? widgetResponse.pieChartConfig : {});
		widget.barChartConfiguration = JSON.stringify(
			widgetResponse.barChartConfiguration ? widgetResponse.barChartConfiguration : {}
		);
		if (widgetResponse.displayOrder) {
			widget.displayOrder = parseInt(widgetResponse.displayOrder, 10);
		}
		widget.scatterConfiguration = JSON.stringify(
			widgetResponse.scatterConfiguration ? widgetResponse.scatterConfiguration : {}
		);
		widget.heatmapEnabled = widgetResponse.heatmapEnabled;
		widget.showThirdPartyRTG = widgetResponse.showthirdpartyrtg;
		widget.showGeofencePoints = UtilityService.isUndefinedOrNull(widgetResponse.showGeofencePoints)
			? true
			: widgetResponse.showGeofencePoints
			? true
			: false;
		widget.showGeofenceOnOverview = UtilityService.isUndefinedOrNull(widgetResponse.showGeofenceOnOverview)
			? false
			: widgetResponse.showGeofenceOnOverview;
		widget.showAllDatesData = UtilityService.isUndefinedOrNull(widgetResponse.showAllDatesData)
			? false
			: widgetResponse.showAllDatesData;
		widget.showGeoCenters = UtilityService.isUndefinedOrNull(widgetResponse.showGeoCenters)
			? true
			: widgetResponse.showGeoCenters
			? true
			: false;
		widget.liftDistributionEnabled = widgetResponse.liftDistributionEnabled;
		widget.liftAreaColor = widgetResponse.liftAreaColor;
		widget.legend = JSON.stringify(widgetResponse.legend ? widgetResponse.legend : []);
		widget.columnsConfig = JSON.stringify(widgetResponse.columnsConfig ? widgetResponse.columnsConfig : {});
		widget.metricsArray = JSON.stringify(widgetResponse.metricsArray ? widgetResponse.metricsArray : []);
		widget.columnsConfig = JSON.stringify(widgetResponse.columnsConfig ? widgetResponse.columnsConfig : []);
		widget.downloadCSVColumns = JSON.stringify(
			widgetResponse.downloadCSVColumns ? widgetResponse.downloadCSVColumns : []
		);
		widget.reportingConfig = JSON.stringify(
			widgetResponse.reportingConfig
				? this.mapReportingConfigToInterface(widgetResponse.reportingConfig)
				: ({} as ReportingConfig)
		);
		widget.downloadConfig = JSON.stringify(
			widgetResponse.downloadConfig ? widgetResponse.downloadConfig : ({} as DownloadConfig)
		);
		widget.permission = this.mapPermissionObjToInterface(widgetResponse.permission);
		widget.permissionWithBooleanCheck = this.mapPermissionWithBooleanCheckObjToInterface(
			widgetResponse.permissionWithBooleanCheck
		);
		widget.removeKeys = JSON.stringify(widgetResponse.removeKeys ? widgetResponse.removeKeys : []);
		widget.creativeStats = JSON.stringify(widgetResponse.creativeStats ? widgetResponse.creativeStats : []);
		widget.tooltipConfigName = widgetResponse.tooltipConfigName;
		widget.uiKitResponseConfig = JSON.stringify(
			widgetResponse.uiKitResponseConfig ? widgetResponse.uiKitResponseConfig : []
		);
		widget.seriesViewConfiguration = JSON.stringify(
			widgetResponse.seriesViewConfiguration
				? this.mapSeriesViewConfiguration(widgetResponse.seriesViewConfiguration)
				: {}
		);
		widget.permissionedQueryKeys = JSON.stringify(
			widgetResponse.permissionedQueryKeys ? widgetResponse.permissionedQueryKeys : []
		);
		widget.queryOrderBy = JSON.stringify(widgetResponse.queryOrderBy ? widgetResponse.queryOrderBy : {});
		widget.downloadCSVOrderBy = JSON.stringify(
			widgetResponse.downloadCSVOrderBy ? widgetResponse.downloadCSVOrderBy : {}
		);
		widget.enableProductDetails = widgetResponse.enableProductDetails;
		widget.mapboxStyles = widgetResponse.mapboxStyles ? widgetResponse.mapboxStyles : '';
		widget.customAccessToken = widgetResponse.customAccessToken ? widgetResponse.customAccessToken : '';
		widget.geoFenceCircleMarkerColor = widgetResponse.geoFenceCircleMarkerColor;
		widget.geoRecencyCircleMarkerColor = widgetResponse.geoRecencyCircleMarkerColor;
		widget.uniqueOverwriteID = this.generateUniqueOverwriteID(widgetResponse);
		widget.geofenceGeovideoCallAttibuteKey = widgetResponse.geofenceGeovideoCallAttibuteKey;
		widget.getDataFromWidget = widgetResponse.getDataFromWidget;
		return widget;
	}

	/**
	 *
	 * @param widgetsResponse
	 * @returns {null}
	 */
	mapNavigationWidgetsResponseToInterface(widgetsResponse: any): Widget[] {
		const widgets: Widget[] = [];

		for (const widgetResponse of widgetsResponse) {
			widgets.push(this.mapNavigationWidgetResponseToInterface(widgetResponse));
		}

		return widgets;
	}

	/**
	 *
	 * @param widgetResponse
	 * @returns {null}
	 */
	mapNavigationWidgetResponseToInterface(widgetResponse: any): Widget {
		const widget: Widget = {} as Widget;
		widget.widget = widgetResponse.widget;
		widget.widgetType = widgetResponse.widgetType;
		widget.widgetClass = widgetResponse.sectionClass;
		widget.description = widgetResponse.description;
		widget.legend = JSON.stringify(widgetResponse.legend ? widgetResponse.legend : []);
		widget.columnsConfig = JSON.stringify(widgetResponse.columnsConfig ? widgetResponse.columnsConfig : {});
		widget.widgetTitle = widgetResponse.sectionName;
		widget.widgetPath = widgetResponse.sectionPath;
		widget.markerIcon = widgetResponse.markerIcon;
		widget.markerIconHover = widgetResponse.markerIconHover;
		widget.containerClass = widgetResponse.containerClass;
		widget.colors = widgetResponse.colors;
		widget.timeseriesKey = widgetResponse.timeseriesKey;
		widget.path = widgetResponse.path;
		widget.productType = widgetResponse.productType;
		widget.query = this.mapQueryResponseToInterface(widgetResponse.query);
		if (widgetResponse.displayOrder) {
			widget.displayOrder = parseInt(widgetResponse.displayOrder, 10);
		}
		widget.permission = this.mapPermissionObjToInterface(widgetResponse.permission);
		widget.permissionWithBooleanCheck = this.mapPermissionWithBooleanCheckObjToInterface(
			widgetResponse.permissionWithBooleanCheck
		);
		widget.downloadConfig = JSON.stringify(
			widgetResponse.downloadConfig ? widgetResponse.downloadConfig : ({} as DownloadConfig)
		);
		widget.downloadCSVColumns = JSON.stringify(
			widgetResponse.downloadCSVColumns ? widgetResponse.downloadCSVColumns : []
		);
		widget.uniqueOverwriteID = this.generateUniqueOverwriteID(widgetResponse);
		return widget;
	}

	/**
	 *
	 * @param queryResponse
	 * @returns {Query}
	 */
	mapQueryResponseToInterface(queryResponse: any): Query {
		const query: Query = {} as Query;
		if (queryResponse) {
			query.key = queryResponse.key;
			query.timeseriesKey = queryResponse.timeseriesKey;
			query.secondaryKey = queryResponse.secondaryKey;
			query.isCustomEndPoint = queryResponse.isCustomEndPoint;
			query.withoutGam = queryResponse.withoutGam;
			query.isSecondaryKeyEndPoint = queryResponse.isSecondaryKeyEndPoint;
			query.tertiaryKey = queryResponse.tertiaryKey;
			query.fields = JSON.stringify(queryResponse.fields ? queryResponse.fields : []);
			query.groupBy = queryResponse.groupBy;
		}
		return query;
	}

	/**
	 *
	 * @param {any[]} colorConfigArray
	 * @returns {ColorConfig[]}
	 */
	mapColorsConfigToInterface(colorConfigArray: any[]): ColorConfig[] {
		const colorsConfig: ColorConfig[] = [];
		for (const colorConfigObj of colorConfigArray) {
			const colorConfig: ColorConfig = {} as ColorConfig;
			colorConfig.color = colorConfigObj.color;
			colorConfig.isHex = colorConfigObj.isHex;
			colorConfig.opacity = colorConfigObj.opacity;
			colorsConfig.push(colorConfig);
		}
		return colorsConfig;
	}

	/**
	 *
	 * @param permissionObj
	 * @returns {PermissionsObj}
	 */
	mapPermissionObjToInterface(permissionResponse: any): PermissionsObj {
		const permissionObj: PermissionsObj = {} as PermissionsObj;
		if (!permissionResponse) {
			permissionObj.isGeneric = true;
			permissionObj.flags = [];
			permissionObj.withoutGam = false;
		} else {
			permissionObj.flags = permissionResponse.flags;
			permissionObj.isGeneric = permissionResponse.isGeneric;
			permissionObj.withoutGam = permissionResponse.withoutGam;
		}
		return permissionObj;
	}

	/**
	 *
	 * @param PermissionsWithBooleanCheckObj
	 * @returns {permissionsWithBooleanCheckObjArray}
	 */
	mapPermissionWithBooleanCheckObjToInterface(permissionWithBooleanCheck: any): PermissionsWithBooleanCheckObj[] {
		const permissionsWithBooleanCheckObjArray: PermissionsWithBooleanCheckObj[] = [];
		if (!permissionWithBooleanCheck) {
			const permissionsWithBooleanCheckObj: PermissionsWithBooleanCheckObj = {} as PermissionsWithBooleanCheckObj;
			permissionsWithBooleanCheckObj.flags = [];
			permissionsWithBooleanCheckObj.flagCheck = false;
			permissionsWithBooleanCheckObjArray.push(permissionsWithBooleanCheckObj);
		} else {
			for (const permissionsBooleanCheckObj of permissionWithBooleanCheck) {
				const permissionsWithBooleanCheckObj: PermissionsWithBooleanCheckObj = {} as PermissionsWithBooleanCheckObj;
				permissionsWithBooleanCheckObj.flags = permissionsBooleanCheckObj.flags;
				permissionsWithBooleanCheckObj.flagCheck = permissionsBooleanCheckObj.flagCheck;
				permissionsWithBooleanCheckObjArray.push(permissionsWithBooleanCheckObj);
			}
			return permissionsWithBooleanCheckObjArray;
		}
		return permissionsWithBooleanCheckObjArray;
	}

	/**
	 *
	 * @param supplementalCampaign
	 * @returns {SupplementalCampaigns}
	 */
	mapSupplementalCampaignsToInterface(supplementalCampaignsObj: any): SupplementalCampaigns {
		const supplementalCampaigns: SupplementalCampaigns = {} as SupplementalCampaigns;
		supplementalCampaigns.permissions = {} as PermissionsObj;
		if (!supplementalCampaignsObj.hasOwnProperty('permissions')) {
			supplementalCampaigns.permissions.isGeneric = true;
			supplementalCampaigns.permissions.flags = [];
		} else {
			const permissionObj: PermissionsObj = supplementalCampaignsObj.permissions;
			supplementalCampaigns.permissions.flags = permissionObj.flags;
			supplementalCampaigns.permissions.isGeneric = permissionObj.isGeneric;
		}
		return supplementalCampaigns;
	}

	/**
	 *
	 * @param datatableResponse
	 * @returns {DataTableConfig}
	 */
	mapDataTableConfigToInterface(datatableResponse: any): DataTableConfig {
		const dataTableConfig: DataTableConfig = {} as DataTableConfig;
		dataTableConfig.enableDownload = datatableResponse.enableDownload;
		dataTableConfig.downloadPNGIcon = datatableResponse.downloadPNGIcon;
		dataTableConfig.downloadFileName = datatableResponse.downloadFileName;
		dataTableConfig.downloadIcon = datatableResponse.downloadIcon;
		dataTableConfig.csvQueryKey = datatableResponse.csvQueryKey;
		dataTableConfig.limit = datatableResponse.limit ? datatableResponse.limit : 10;
		dataTableConfig.rowHeight = datatableResponse.rowHeight ? datatableResponse.rowHeight : 50;
		dataTableConfig.headerHeight = datatableResponse.headerHeight ? datatableResponse.headerHeight : 50;
		dataTableConfig.footerHeight = datatableResponse.footerHeight ? datatableResponse.footerHeight : 50;
		dataTableConfig.isVerticalScrollable = datatableResponse.isVerticalScrollable;
		dataTableConfig.sortBy = datatableResponse && datatableResponse.sortBy ? datatableResponse.sortBy : 'impressions';
		dataTableConfig.columns = this.mapColumnConfigResponseToInterface(datatableResponse.columns);
		dataTableConfig.downloadPNGIcon = datatableResponse.downloadPNGIcon ? true : false;
		dataTableConfig.newTabCsvQueryKey = datatableResponse.newTabCsvQueryKey;
		dataTableConfig.newTabCsvFileName = datatableResponse.newTabCsvFileName;
		if (datatableResponse.permission) {
			dataTableConfig.permission = datatableResponse.permission;
		}
		return dataTableConfig;
	}

	/**
	 *
	 * @param datatableResponse
	 * @returns Array<{DataTableConfig}>
	 */
	mapMultiDataTableConfigToInterface(datatableResponse: any): DataTableConfig[] {
		const multiDataTableConfig: DataTableConfig[] = [];
		for (const response of datatableResponse) {
			multiDataTableConfig.push(this.mapDataTableConfigToInterface(response));
		}
		return multiDataTableConfig;
	}

	/**
	 *
	 * @param columnConfigsResponse
	 * @returns {ColumnConfig[]}
	 */
	mapColumnConfigResponseToInterface(columnConfigsResponse: any[]): ColumnConfig[] {
		const columnConfigs: ColumnConfig[] = [];
		for (const columnConfigResponse of columnConfigsResponse) {
			const columnConfig: ColumnConfig = {} as ColumnConfig;
			columnConfig.columnName = columnConfigResponse.columnName;
			columnConfig.columnNameShort = columnConfigResponse.columnNameShort;
			columnConfig.columnHeaderClass = columnConfigResponse.columnHeaderClass;
			columnConfig.columnCellClass = columnConfigResponse.columnCellClass;
			columnConfig.columnType = columnConfigResponse.columnType;
			columnConfig.columnKey = columnConfigResponse.columnKey;
			columnConfig.columnFormat = columnConfigResponse.columnFormat;
			columnConfig.columnOrder = columnConfigResponse.columnOrder;
			columnConfig.columnHasProgressBar = columnConfigResponse.columnHasProgressBar;
			columnConfig.columnSuffix = columnConfigResponse.columnSuffix ? columnConfigResponse.columnSuffix : null;
			columnConfig.columnShowTitle = columnConfigResponse.columnShowTitle;
			columnConfig.columnPrepend = columnConfigResponse.columnPrepend ? columnConfigResponse.columnPrepend : '';
			columnConfig.columnSuffix = columnConfigResponse.columnSuffix ? columnConfigResponse.columnSuffix : null;
			columnConfig.columnHasTooltip = !!columnConfigResponse.columnTooltip;
			columnConfig.columnTooltip = columnConfig.columnHasTooltip ? columnConfigResponse.columnTooltip : '';
			columnConfig.columnDisplayImage = columnConfigResponse.columnDisplayImage;
			columnConfig.columnFlexGrow = columnConfigResponse.columnFlexGrow ?? 1;
			columnConfig.columnCheckboxable = columnConfigResponse.columnCheckboxable ? true : false;
			columnConfig.headerCheckboxable = columnConfigResponse.headerCheckboxable ? true : false;
			columnConfig.hasComplexProperty = columnConfigResponse.hasComplexProperty;
			if (columnConfigResponse.permission) {
				columnConfig.permission = columnConfigResponse.permission;
			}
			if (columnConfigResponse.permissionWithBooleanCheck) {
				columnConfig.permissionWithBooleanCheck = columnConfigResponse.permissionWithBooleanCheck;
			}
			columnConfig.complexProperty = columnConfigResponse.complexProperty
				? this.mapComplexPropertyToInterface(columnConfigResponse.complexProperty)
				: undefined;
			columnConfigs.push(columnConfig);
		}
		return columnConfigs;
	}

	/**
	 *
	 * @param reportingConfigResponse
	 * @returns {ReportingConfig}
	 */
	mapReportingConfigToInterface(reportingConfigResponse: any): ReportingConfig {
		const reportingConfig: ReportingConfig = {} as ReportingConfig;
		reportingConfig.attributes = this.mapReportingConfigObjectToInterface(
			reportingConfigResponse.attributes ? reportingConfigResponse.attributes : []
		);
		reportingConfig.metrics = this.mapReportingConfigObjectToInterface(
			reportingConfigResponse.metrics ? reportingConfigResponse.metrics : []
		);
		return reportingConfig;
	}

	/**
	 *
	 * @param reportingConfigObjectArrayResponse
	 * @returns {ReportingConfigObject[]}
	 */
	mapReportingConfigObjectToInterface(reportingConfigObjectArrayResponse: any): ReportingConfigObject[] {
		const reportingObjectArray: ReportingConfigObject[] = [];
		for (const reportingConfigObjectResp of reportingConfigObjectArrayResponse) {
			const reportingObject: ReportingConfigObject = reportingConfigObjectResp as ReportingConfigObject;
			reportingObject.isGeneric = reportingObject.dependentDataSource
				? reportingObject.dependentDataSource.length === 0
				: true;
			reportingObjectArray.push(reportingObject);
		}
		return reportingObjectArray;
	}

	mapPrintableReportResponseToInterface(printableReportResponse: any): PrintableReport {
		const printableReportConfig: PrintableReport = {} as PrintableReport;
		if (printableReportResponse !== undefined) {
			printableReportConfig.strategyName = printableReportResponse.strategyName;
			printableReportConfig.coverPageEnabled = printableReportResponse.coverPageEnabled;
			printableReportConfig.slideWidgetDataConfiguration = this.mapSlideWidgetDataConfiguration(
				printableReportResponse.slideWidgetDataConfiguration
			);
			printableReportConfig.permission = this.mapPermissionObjToInterface(printableReportResponse.permission);
			printableReportConfig.supplementalCampaigns = printableReportResponse.supplementalCampaigns
				? this.mapSupplementalCampaignsToInterface(printableReportResponse.supplementalCampaigns)
				: undefined;
			printableReportConfig.uniqueOverwriteID = UtilityService.isUndefinedOrNull(
				printableReportResponse.uniqueOverwriteID
			)
				? null
				: String(printableReportResponse.uniqueOverwriteID);
		}
		return printableReportConfig;
	}

	mapSlideWidgetDataConfiguration(slideConfigurationResponse: any): SlideWidgetDataConfiguration {
		const slideConfiguration: SlideWidgetDataConfiguration = {} as SlideWidgetDataConfiguration;
		for (const slideConfigResponse in slideConfigurationResponse) {
			if (slideConfigurationResponse.hasOwnProperty(slideConfigResponse)) {
				slideConfiguration[slideConfigResponse] = this.mapSlideDataConfiguration(
					slideConfigurationResponse[slideConfigResponse]
				);
			}
		}
		return slideConfiguration;
	}

	mapSlideDataConfiguration(slideDataResponse: any): SlideDataConfiguration {
		const slideDataConfiguration: SlideDataConfiguration = {} as SlideDataConfiguration;
		slideDataConfiguration.displayName = slideDataResponse.displayName;
		slideDataConfiguration.value = slideDataResponse.value;
		slideDataConfiguration.isDisabled = slideDataResponse.isDisabled;
		slideDataConfiguration.permissions = slideDataResponse.permissions;
		return slideDataConfiguration;
	}

	mapComplexPropertyToInterface(complexPropertyObj: any): ComplexPropertyConfiguration[] {
		const complexProperties = [];
		for (const propertyObj of complexPropertyObj) {
			const complexProperty = {} as ComplexPropertyConfiguration;
			complexProperty.propertyKey = propertyObj.propertyKey;
			complexProperty.propertyType = propertyObj.propertyType;
			complexProperty.propertyFormat = propertyObj.propertyFormat;
			complexProperty.cssClass = propertyObj.cssClass ? propertyObj.cssClass : `grouped-col`;
			complexProperty.styles = propertyObj.styles ? propertyObj.styles : {};
			complexProperty.prepend = propertyObj.prepend ? propertyObj.prepend : '';
			complexProperty.postpend = propertyObj.postpend ? propertyObj.postpend : '';
			complexProperties.push(complexProperty);
		}
		return complexProperties;
	}

	mapSeriesViewConfiguration(seriesViewObj: any): SeriesViewConfiguration {
		const seriesViewConfig: SeriesViewConfiguration = {} as SeriesViewConfiguration;
		seriesViewConfig.radioGroupSectionName =
			seriesViewObj.radioGroupSectionName !== undefined ? seriesViewObj.radioGroupSectionName : undefined;
		seriesViewConfig.checkboxGroupSectionName =
			seriesViewObj.checkboxGroupSectionName !== undefined ? seriesViewObj.checkboxGroupSectionName : undefined;
		seriesViewConfig.defaultRadioButtonSelected =
			seriesViewObj.defaultRadioButtonSelected !== undefined ? seriesViewObj.defaultRadioButtonSelected : 0;

		if (seriesViewObj.checkboxButtons !== undefined && seriesViewObj.checkboxButtons.length > 0) {
			const checkboxButtonsGroup: CheckboxButton[] = [];
			for (const [index, checkBox] of seriesViewObj.checkboxButtons.entries()) {
				const checkboxButtonObj: CheckboxButton = {} as CheckboxButton;
				checkboxButtonObj.displayName = checkBox.displayName !== undefined ? checkBox.displayName : `Series ${index}`;
				checkboxButtonObj.seriesId = checkBox.seriesId !== undefined ? checkBox.seriesId : `series-${index}`;
				checkboxButtonObj.color = checkBox.color !== undefined ? checkBox.color : '';
				checkboxButtonObj.visible = checkBox.visible !== undefined ? checkBox.visible : true;
				if (checkBox.permission) {
					checkboxButtonObj.permission = checkBox.permission;
				}
				checkboxButtonsGroup.push(checkboxButtonObj);
			}
			seriesViewConfig.checkboxButtons = checkboxButtonsGroup;
		}

		if (seriesViewObj.radioButtonGroup !== undefined && seriesViewObj.radioButtonGroup.length > 0) {
			const radioButtonGroup: RadioButtonGroup[] = [];
			for (const [index, radioButton] of seriesViewObj.radioButtonGroup.entries()) {
				const radioButtonObj: RadioButtonGroup = {} as RadioButtonGroup;
				radioButtonObj.displayName =
					radioButton.displayName !== undefined ? radioButton.displayName : `Series ${index}`;
				radioButtonObj.seriesId = radioButton.seriesId !== undefined ? radioButton.seriesId : `series-${index}`;
				radioButtonObj.color = radioButton.color !== undefined ? radioButton.color : '';
				radioButtonObj.title = radioButton.title !== undefined ? radioButton.title : '';
				radioButtonGroup.push(radioButtonObj);
			}
			seriesViewConfig.radioButtonGroup = radioButtonGroup;
		}
		return seriesViewConfig;
	}

	createStringIfExist(uniqueID: string, propertyToAdd: string, seperatorToUse: string): string {
		return !UtilityService.isUndefinedOrNull(propertyToAdd)
			? propertyToAdd.includes('only')
				? propertyToAdd
				: uniqueID + propertyToAdd + seperatorToUse
			: uniqueID + seperatorToUse;
	}

	/***
	 * For overriding or replacing, we need to form proper Unique ID.
	 * For this reason, we need to have similar fields as the parent config,
	 * 			- uniqueOverwriteID
	 * 			- path
	 * 			- sectionPath
	 * 			- widget
	 * 			- widgetType
	 * 	[update]: UniqueID should be passed	from config, User needs to maintain uniqueness on their own.
	 */
	generateUniqueOverwriteID(widgetResponse: any): string {
		// const propertiesForUniqueID = ['uniqueOverwriteID', 'path', 'sectionPath', 'widget', 'widgetType'];
		// let uniqueID = '';
		// propertiesForUniqueID.forEach((property) => {
		// 	uniqueID = this.createStringIfExist(uniqueID, String(widgetResponse[property]), '&&');
		// });
		// returning uniqueID as is. So users needs to be aware of the uniqueness of these ID's
		return widgetResponse['uniqueOverwriteID'] ? String(widgetResponse['uniqueOverwriteID']) : null;
	}

	validateArrayElementsType(elements: any[], typeCheck: any): boolean {
		return typeof elements[0] === typeCheck;
	}

	isPropertyEmptyObject(item: any): boolean {
		const typesArray = ['boolean', 'number', 'float'];
		if (typesArray.includes(typeof item)) {
			return true;
		}
		try {
			const json = JSON.parse(item);
			const allowedInChild = typesArray.includes(typeof json);
			return _.isEmpty(json) && !allowedInChild ? false : true;
		} catch (e) {
			return true;
		}
	}

	/***
	 * @param1 defaultConfig: default config to inherit from
	 * @param2 partnerConfig: child config to inherit into
	 * @return finalConfig: a derived config based on requirements
	 */
	mergeParentChildConfig(defaultConfig: any, partnerConfig: any): any {
		let finalConfig = {};
		for (const [key, value] of Object.entries(defaultConfig)) {
			finalConfig[key] = this.mergeConfigsOnKey(defaultConfig, partnerConfig, key);
		}
		// Now copy over any child property that was not there in Parent.
		finalConfig = {
			...partnerConfig,
			...finalConfig
		};
		return finalConfig;
	}

	mergeConfigsOnKey(parent: any, child: any, key: any): any {
		const baseConditionReturnTypes = ['string', 'boolean', 'number', 'float'];
		const isUndefinedReturnTypes = ['undefined'];

		// if child key doesn't exist, if the key exist but its an empty array or object and not a string, bool or number, then return parent
		if ((child.hasOwnProperty(key) && !this.isPropertyEmptyObject(child[key])) || !child.hasOwnProperty(key)) {
			return parent[key];
		} else if (
			baseConditionReturnTypes.concat(isUndefinedReturnTypes).includes(typeof parent[key]) && // if child has property then use that
			baseConditionReturnTypes.includes(typeof child[key]) &&
			!isUndefinedReturnTypes.includes(typeof child[key])
		) {
			return child[key];
		} else if (
			(baseConditionReturnTypes.includes(typeof parent[key]) ||
				parent[key] instanceof Array ||
				parent[key] instanceof Object) && // if child does not have property
			isUndefinedReturnTypes.includes(typeof child[key])
		) {
			return parent[key];
		} else if (parent[key] instanceof Array && child[key] instanceof Array) {
			const isStringType =
				parent[key].length >= child[key].length
					? this.validateArrayElementsType(parent[key], 'string')
					: this.validateArrayElementsType(child[key], 'string');
			// if string array also has values in child config then merge both values.
			return isStringType ? child[key] : this.mergeParentChildArray(parent[key], child[key]);
		} else if (parent[key] instanceof Object && child[key] instanceof Object && child[key] !== null) {
			return this.mergeParentChildConfig(parent[key], child[key]);
		} else {
			return null;
		}
	}

	/***
	 * @param1 defaultConfigArray
	 * @param2 partnerConfigArray
	 * @return Array: merged or replaced of partnerConfig array with the defaultConfig array.
	 *
	 */
	mergeParentChildArray(defaultConfigArray: any[], partnerConfigArray: any[]): any[] {
		// get all the widgets from parent replace it if child widgets on same unique ID.
		// only returns merged configs.
		const mergedPropertiesOnly = this.mergeArrayElementsWithConfigProperty(
			defaultConfigArray,
			partnerConfigArray,
			'uniqueOverwriteID'
		);

		// *********** COMMENTED BECAUSE WE DO NOT WANT TO SUPPORT THIS FEATURE RIGHT NOW ************
		// first map widget configs that are entirely need to be replaced.
		// const replacedProperties = this.replaceArrayElementsWithConfigProperty(
		// 	defaultConfigArray,
		// 	partnerConfigArray,
		// 	'uniqueOverwriteID',
		// 	'replaceOnOverwriteID',
		// 	false
		// );

		// merged configs from the default config.
		const derivedProperties = this.replaceArrayElementsWithConfigProperty(
			defaultConfigArray,
			mergedPropertiesOnly,
			'uniqueOverwriteID'
		);

		// any remaining widgets from child that parent doesn't have.
		const remainingChildProperties = partnerConfigArray.filter((obj) => {
			return !defaultConfigArray.find((o) => o.uniqueOverwriteID === obj.uniqueOverwriteID);
		});

		// After getting a final array we are ordering it on display property.
		return _.sortBy(remainingChildProperties.concat(derivedProperties), 'displayOrder');
	}

	mergeArrayElementsWithConfigProperty(
		parentArray: any[],
		childArray: any[],
		idToMergeOn: string,
		replaceFlag?: string
	): any {
		const mergedConfigsOnly = [];
		parentArray.forEach((obj) =>
			childArray.find((child: any) => {
				if (
					!UtilityService.isUndefinedOrNull(child[idToMergeOn]) &&
					!UtilityService.isUndefinedOrNull(obj[idToMergeOn]) &&
					child[idToMergeOn] === obj[idToMergeOn]
				) {
					mergedConfigsOnly.push(this.mergeParentChildConfig(obj, child));
				}
			})
		);
		return mergedConfigsOnly;
	}

	replaceArrayElementsWithConfigProperty(
		parentArray: any[],
		childArray: any[],
		idToMergeOn: string,
		replaceFlag?: string,
		doReplace?: boolean
	): any {
		return parentArray.map(
			(obj) =>
				childArray.find((child: any) => {
					return (
						!UtilityService.isUndefinedOrNull(child[idToMergeOn]) &&
						!UtilityService.isUndefinedOrNull(obj[idToMergeOn]) &&
						// (!UtilityService.isUndefinedOrNull(child[replaceFlag]) || child[replaceFlag] || doReplace) &&
						child[idToMergeOn] === obj[idToMergeOn]
					);
				}) || obj
		);
	}
}
